import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getGameType } from '../../variables/gameTypes';
import IconAdd from '../../components/icons/IconAdd';

const post = (payload) => axios.post('/api/gridcard', JSON.stringify(payload), { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
const put = (id, payload) => axios.put('/api/gridcard/' + id, JSON.stringify(payload), { withCredentials: true, headers: { 'Content-Type': 'application/json' } });

// Type-aware card list for a single game (Grid) instance: value-only types
// (Grid Numbers/Letters) get a chip list with a bulk-paste add; value+answer
// types (Quiz/Matching) get a Q/A row list. Server-backed (POST/PUT/DELETE
// per action) rather than TagsEditor's buffered-local-array approach, since
// each card is its own DB row that other things could reference.
export default function GameCardsEditor({ gridId, type }) {
   const navigate = useNavigate();
   const gameType = getGameType(type);
   const hasAnswer = gameType.hasAnswer;

   const [cards, setCards] = useState([]);
   const [loaded, setLoaded] = useState(false);

   const [newValue, setNewValue] = useState('');
   const [newAnswer, setNewAnswer] = useState('');
   const [bulkMode, setBulkMode] = useState(false);
   const [bulkText, setBulkText] = useState('');
   const [busy, setBusy] = useState(false);

   const [editingId, setEditingId] = useState(null);
   const [editValue, setEditValue] = useState('');
   const [editAnswer, setEditAnswer] = useState('');

   useEffect(() => {
      axios.get('/api/gridcard?grid=' + gridId, { withCredentials: true })
         .then(response => { setCards(response.data || []); setLoaded(true); })
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
            setLoaded(true);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [gridId]);

   const handleAdd = () => {
      const value = newValue.trim();
      if(!value || busy) return;
      const payload = hasAnswer ? { value, answer: newAnswer.trim(), grid: Number(gridId) } : { value, grid: Number(gridId) };
      setBusy(true);
      post(payload).then(response => {
         setCards(prev => [...prev, response.data]);
         setNewValue(''); setNewAnswer(''); setBusy(false);
      }).catch(() => setBusy(false));
   };

   const handleBulkAdd = () => {
      const lines = [...new Set(bulkText.split('\n').map(l => l.trim()).filter(Boolean))];
      if(lines.length === 0 || busy) return;
      setBusy(true);
      Promise.all(lines.map(value => post({ value, grid: Number(gridId) })))
         .then(responses => {
            setCards(prev => [...prev, ...responses.map(r => r.data)]);
            setBulkText(''); setBulkMode(false); setBusy(false);
         }).catch(() => setBusy(false));
   };

   const handleDelete = (id) => {
      axios.delete('/api/gridcard/' + id, { withCredentials: true })
         .then(() => setCards(prev => prev.filter(c => c.id !== id)))
         .catch(() => window.alert('Не вдалося видалити елемент'));
   };

   const startEdit = (card) => {
      setEditingId(card.id);
      setEditValue(card.value || '');
      setEditAnswer(card.answer || '');
   };
   const cancelEdit = () => setEditingId(null);
   const saveEdit = (id) => {
      const value = editValue.trim();
      if(!value) return;
      const payload = hasAnswer ? { value, answer: editAnswer.trim(), grid: Number(gridId) } : { value, grid: Number(gridId) };
      put(id, payload).then(response => {
         setCards(prev => prev.map(c => c.id === id ? response.data : c));
         setEditingId(null);
      }).catch(() => window.alert('Не вдалося зберегти зміни'));
   };

   if(!loaded) return <div className="games-empty">Завантаження...</div>;

   return (
      <div className="game-cards-editor">
         <div className="game-cards-add-row">
            <input className="game-cards-input" type="text" value={newValue}
               placeholder={gameType.valuePlaceholder}
               onChange={e => setNewValue(e.target.value)}
               onKeyDown={e => { if(e.key === 'Enter' && (!hasAnswer)) handleAdd(); }} />
            {hasAnswer ? (
               <input className="game-cards-input" type="text" value={newAnswer}
                  placeholder={gameType.answerPlaceholder}
                  onChange={e => setNewAnswer(e.target.value)}
                  onKeyDown={e => { if(e.key === 'Enter') handleAdd(); }} />
            ) : ''}
            <div className="game-cards-add-btn" onClick={handleAdd} title="Додати">
               <IconAdd />
            </div>
            {!hasAnswer ? (
               <div className="game-cards-bulk-toggle" onClick={() => setBulkMode(prev => !prev)}>
                  {bulkMode ? 'Сховати масове додавання' : 'Додати список'}
               </div>
            ) : ''}
         </div>

         {bulkMode ? (
            <div className="game-cards-bulk-panel">
               <textarea className="game-cards-bulk-textarea" rows={4} value={bulkText}
                  placeholder={'По одному елементу на рядок, наприклад:\napple\nbanana\ncherry'}
                  onChange={e => setBulkText(e.target.value)} />
               <button type="button" className="game-cards-bulk-add-btn" disabled={busy} onClick={handleBulkAdd}>
                  Додати всі
               </button>
            </div>
         ) : ''}

         {cards.length === 0 ? (
            <div className="games-empty">Ще немає елементів — додайте перший вище</div>
         ) : hasAnswer ? (
            <div className="game-cards-qa-list">
               {cards.map(card => (
                  editingId === card.id ? (
                     <div className="game-cards-qa-row editing" key={card.id}>
                        <input className="game-cards-input" value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus />
                        <input className="game-cards-input" value={editAnswer} onChange={e => setEditAnswer(e.target.value)} />
                        <div className="game-cards-row-actions">
                           <button type="button" className="game-cards-row-save" onClick={() => saveEdit(card.id)}>✓</button>
                           <button type="button" className="game-cards-row-cancel" onClick={cancelEdit}>×</button>
                        </div>
                     </div>
                  ) : (
                     <div className="game-cards-qa-row" key={card.id} onClick={() => startEdit(card)}>
                        <div className="game-cards-qa-value">{card.value}</div>
                        <div className="game-cards-qa-answer">{card.answer}</div>
                        <div className="game-cards-row-actions" onClick={e => e.stopPropagation()}>
                           <button type="button" className="game-cards-row-delete" onClick={() => handleDelete(card.id)}>×</button>
                        </div>
                     </div>
                  )
               ))}
            </div>
         ) : (
            <div className="game-cards-chip-list">
               {cards.map(card => (
                  editingId === card.id ? (
                     <div className="game-cards-chip editing" key={card.id}>
                        <input className="game-cards-chip-input" value={editValue}
                           onChange={e => setEditValue(e.target.value)}
                           onKeyDown={e => { if(e.key === 'Enter') saveEdit(card.id); if(e.key === 'Escape') cancelEdit(); }}
                           autoFocus onBlur={() => saveEdit(card.id)} />
                     </div>
                  ) : (
                     <div className="game-cards-chip" key={card.id} onClick={() => startEdit(card)}>
                        {card.value}
                        <span className="game-cards-chip-remove" onClick={e => { e.stopPropagation(); handleDelete(card.id); }}>×</span>
                     </div>
                  )
               ))}
            </div>
         )}
      </div>
   );
}
